import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { immer } from 'zustand/middleware/immer'
import { merge } from 'lodash'
import { toast } from 'react-toastify'
import { z } from 'zod'
import moment from 'moment'
import localforage from 'localforage';
import api from '../../actions/api'
import __ from "../../helpers/translation";

localforage.config({
  driver: localforage.INDEXEDDB,
})

//#region Schema validation

  /**
   * The answerSchema defines the structure of an individual answer.
   * It includes all fields for questions and the corresponding answers within a report.
   * 
   * @constant {z.ZodObject}
   */
  export const answerSchema = z.object({
    answer: z.unknown().nullable().describe('The actual answer to the question. Type depends on the question type.'),
    created_at: z.string().nullable().optional().describe('The date and time when the answer was created in the database.'),
    id: z.number().int().positive().describe('The unique identifier of the answer.'),
    inspector_remark: z.string().nullable().optional().describe('The inspector\'s remark for the answer.'),
    moment: z.number().int().min(0),  // assuming moment has only positive values
    no_next_step: z.number().int().min(0),   // assuming it's an integer
    parent_question_id: z.number().int().positive().nullable().optional(),
    photos: z.number().int().min(0),  // assuming it's an integer count
    question_data: z.object({
      number: z.string().nullable(),
      question: z.string().nullable(),
      remark: z.string().nullable(),
      requirement: z.string().nullable(),
      stop_reason: z.string().nullable(),
    }),
    question_id: z.number().int().positive().optional(),
    report_id: z.number().int().positive().optional(),
    sort_order: z.number().int().min(0),  // assuming this represents an order index
    status: z.number().int().min(0),
    type: z.number().int().min(0),  // depending on the valid range of 'type'
    yes_next_step: z.number().int().min(0),  // assuming it's an integer
    updated_at: z.string().nullable().optional().describe('The date and time when the answer was last updated in the database.'),
  }).strict();

  /**
   * The reportDataSchema defines the structure of a report.
   * It includes all the fields and their respective types for a report,
   * and also validates the relation IDs and the answers section.
   * 
   * @constant {z.ZodObject}
   */
  export const reportDataSchema = z.object({
    id: z.number().int().positive(),
    version: z.number().int().positive(),
    removal: z.string().min(1),
    can_send_email: z.number().int().min(0).max(1),
    relation_ids: z.object({
      company_id: z.number().int().positive(),
      location_id: z.number().int().positive(),
      sublocation_id: z.number().int().positive(),
      removal_type_id: z.number().int().positive(),
    }).partial(),
    answers: z.array(answerSchema),
  });

  export const reportStopInfoSchema = z.object({
    explanation: z.string().nullable().describe('The explanation for stopping the report.'),
    generateReport: z.boolean().default(false).describe('Flag to indicate if the report should be generated.'),
    emails: z.string().nullable().describe('The email addresses to send the report to (comma-separated).'),
  }).optional().describe('The stop information of the report.');

  /**
   * The reportStatus defines the possible statuses of a report.
   * 
   * @constant {Object} reportStatus
   * @property {string} INACTIVE - The report is inactive and cached for future use.
   * @property {string} ACTIVE - The report is currently active and questions are being answered.
   * @property {string} COMPLETED_OFFLINE - The report is completed offline and is ready for synchronization.
   */
  export const reportStatus = {
    INACTIVE: 'inactive',
    ACTIVE: 'active',
    COMPLETED_OFFLINE: 'completed_offline',
    STOPPED: 'stopped',
  }

  /**
   * The reportSchema includes all data for a report, as well as the state of the report.
   * The state is splitted from the data, to respect the integrity of the report data
   * as how it is expected to be structured on the server-side.
   * 
   * @constant {z.ZodObject}
   */
  export const reportSchema = z.object({
    status: z.union([
      z.literal(reportStatus.INACTIVE).describe('The report is inactive and cached for future use.'),
      z.literal(reportStatus.ACTIVE).describe('The report is currently active and questions are being answered.'),
      z.literal(reportStatus.COMPLETED_OFFLINE).describe('The report is completed offline and is ready for synchronization.'),
      z.literal(reportStatus.STOPPED).describe('The report is stopped and is not active anymore.'),
    ]),
    data: reportDataSchema,
    stopInfo: reportStopInfoSchema,
  })

//#endregion

//#region Helper Functions

  /**
   * Starts the report generation job on the server.
   *
   * @param {Object} company - Company object containing the company ID.
   * @param {Object} user - User object containing the user ID.
   * @param {Object} location - Location object containing the location ID.
   * @param {number[]} sublocationIds - Array of sublocation IDs.
   * @returns {Promise<string|null>} - The job ID.
   * @throws Will throw an error if the API call fails.
   */
  async function startReportGenerationJob(company, user, location, sublocationIds) {
    try {
      const response = await window.axios.post(api.questionaires.generateReport, {
        company_id: company.id,
        user_id: user.id,
        location_id: location.id,
        sublocations: sublocationIds,
      });

      if(response.data.status === 'insufficient_credits') {
        toast.error(__('app.credits.not_enough_credits_for_scheduling', {
          'credits': response.data.meta.credits,
          'needed_credits': response.data.meta.needed_credits,
        }))
        return null;
      }

      if (response.data.code !== 200 || response.data.status !== 'success') {
        throw new Error('Could not start the report generation job on the server.');
      }

      return response.data.job_id;
    } 
    catch (error) {
      console.error('Error starting report generation job:', error);
      toast.error(__('app.toasts.error_failed_to_start_job'));
      return null;
    }
  }

  /**
   * Polls the job status until it's completed or failed.
   *
   * @param {string} jobId - The job ID to poll.
   * @returns {Promise<void>}
   * @throws Will throw an error if the job fails or times out.
   */
  async function pollJobStatus(jobId) {
    const maxRetries = 200; // Maximum number of polling attempts
    const delay = 3000; // Delay between polls in milliseconds
    let retries = 0;

    while (retries < maxRetries) {
      await new Promise((resolve) => setTimeout(resolve, delay));

      try {
        const statusResponse = await checkJobStatus(jobId);

        if (!statusResponse) {
          retries++;
          continue;
        }

        if (statusResponse.job_status === 'processed') {
          await handleJobSuccess(statusResponse.job_data);
          return;
        } else if (statusResponse.job_status === 'failed') {
          throw new Error('Job failed on the server.');
        }

        retries++;
      } catch (error) {
        console.error('Error checking job status:', error);
        throw error;
      }
    }

    // Timed out
    throw new Error(__('app.toasts.error_timed_out_while_generating_reports'));
  }

  /**
   * Handles the successful completion of the job.
   *
   * @param {string} jobData - The job data returned from the server.
   * @returns {Promise<void>}
   * @throws Will throw an error if parsing fails.
   */
  async function handleJobSuccess(jobData) {
    try {
      const parsedData = JSON.parse(jobData);
      console.log('Parsed job data:', parsedData);

      const parsed = z.array(reportDataSchema).safeParse(parsedData);

      if (!parsed.success) {
        console.error('Could not parse scheduled reports:', parsed.error);
        throw new Error(__('app.toasts.error_invalid_report_data'));
      }

      if(parsed.data.length === 0) {
        console.warn('No reports were generated by the job on the server.');
        throw new Error(__('app.toasts.error_scheduling_no_locations_found'));
      }

      useScheduledReportStore.setState((state) => {
        state.scheduledReports = parsed.data.map((report) => ({
          status: reportStatus.INACTIVE,
          data: {
            ...report,
            answers: sortAnswersWithSubquestions(report.answers),
          },
        }))
        state.lastSync = moment().unix();
      });

      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } 
    catch (error) {
      console.error('Error handling job success:', error);
      toast.error(error.message || __('app.toasts.error_scheduling'));
      throw error;
    }
  }

  /**
   * Checks the job status by making an API call.
   *
   * @param {string} jobId - The job ID to check.
   * @returns {Promise<Object>} - The job status response.
   * @throws Will throw an error if the API call fails.
   */
  async function checkJobStatus(jobId) {
    try {
      const url = api.jobs.check.replace(':jobId', jobId);
      const response = await window.axios.get(url);
      return response.data;
    } catch (error) {
      console.error('Error checking job status:', error);
      throw error;
    }
  }

  /**
   * Sorts answers so that subquestions are placed immediately after their parent questions.
   * First sorts parent questions by `moment` and `sort_order`, then inserts each subquestion
   * directly after its parent question.
   * 
   * @param {Array} answers - The array of answer objects to sort.
   * @returns {Array} - The sorted array with subquestions following their parent questions.
   */
  const sortAnswersWithSubquestions = (answers) => {

    // Separate parent questions and subquestions
    const parentQuestions = answers.filter(answer => answer.parent_question_id === null);
    const subquestions = answers.filter(answer => answer.parent_question_id !== null);

    // Sort parent questions by `moment` and `sort_order`
    parentQuestions.sort((a, b) => {
      if (a.moment !== b.moment) {
        return a.moment - b.moment;
      }
      return a.sort_order - b.sort_order;
    });

    // Create a map for subquestions keyed by `parent_question_id` for fast lookup
    const subquestionMap = new Map();
    subquestions.forEach(subquestion => {
      subquestionMap.set(subquestion.parent_question_id, subquestion);
    });

    // Build the final sorted list by inserting each subquestion after its parent
    const sortedAnswers = [];
    parentQuestions.forEach(parent => {
      sortedAnswers.push(parent);
      // Check if this parent has a corresponding subquestion
      const subquestion = subquestionMap.get(parent.question_id);
      if (subquestion) {
        sortedAnswers.push(subquestion);
      }
    });

    return sortedAnswers;
  };

//#endregion

/**
 * Zustand store for managing scheduled reports.
 * 
 * This store uses the `persist` middleware to save the state to `localStorage` and the `immer` middleware to handle immutable state updates.
 * 
 * @module useScheduledReportStore
 * 
 * @typedef {Object} ScheduledReportStore
 * @property {Array} scheduledReports - An array of scheduled reports.
 * @property {number|null} lastSync - The last time the scheduled reports were synced with the server (Unix timestamp in seconds).
 * @property {Function} fetchScheduledReports - Asynchronous function to fetch scheduled reports from the server.
 * 
 * @function useScheduledReportStore
 * @returns {ScheduledReportStore} The Zustand store for scheduled reports.
 * 
 * @example
 * import { useScheduledReportStore } from 'path/to/scheduledReport';
 * 
 * const { scheduledReports, lastSync, fetchScheduledReports } = useScheduledReportStore();
 * 
 * // Fetch scheduled reports
 * await fetchScheduledReports();
 */
export const useScheduledReportStore = create()(
  persist(
    immer(
      (set, get) => ({

        /**
         * An array of scheduled reports. For the type of these reports,
         * refer to the `reportSchema` constant.
         */
        scheduledReports: [],

        /**
         * The last time the scheduled reports were synced with the server.
         * Unix timestamp in seconds. 
         */
        lastSync: null,

        /**
         * Flag to indicate if a scheduling job is currently running. If so,
         * it should fetch to the API to get the status of the job and update the 
         * store with the new scheduled reports if the job completes.
         */
        schedulingJobActive: false,

        /**
         * Generates offline reports by calling the generateReport API and polls the job status.
         * Stores the reports in the store after parsing with Zod schema.
         *
         * @param {Object} company - Company object containing the company ID.
         * @param {Object} user - User object containing the user ID.
         * @param {Object} location - Location object containing the location ID.
         * @param {number[]} sublocationIds - Array of sublocation IDs.
         * @returns {Promise<void>}
         * @throws Will throw an error if the reports cannot be generated.
         */
        generateOfflineReports: async (company, user, location, sublocationIds) => {

          if (!window.navigator.onLine) {
            toast.error(__('app.toasts.error_no_internet_connection'));
            throw new Error('No internet connection.');
          }
  
          if (get().schedulingJobActive) {
            toast.warn(__('app.toasts.warning_wait_on_other_schedule_job'));
            return;
          }
  
          set((state) => { state.schedulingJobActive = true });
  
          try {
            const jobId = await startReportGenerationJob(company, user, location, sublocationIds);
            if(jobId !== null) {
              await pollJobStatus(jobId);
            }
          } 
          catch (error) {
            console.error('Error generating reports:', error);
            throw error;
          } 
          finally {
            set((state) => { state.schedulingJobActive = false });
          }
        },

        /**
         * Adds a report to the scheduled reports. If a report with the same ID exists, it overwrites it.
         *
         * @param {Object} report - The report object to add. Should adhere to the `reportSchema`.
         */
        addReport: (report) => {
          set((state) => {

            const parsed = reportSchema.safeParse(report);
            if (!parsed.success) {
              console.error('Invalid report object:', parsed.error);
              toast.error(__('app.toasts.error_invalid_report_data'));
              return;
            }

            const existingIndex = state.scheduledReports.findIndex(r => {
              const existingReportRelationIds = JSON.stringify(r.data.relation_ids);
              const newReportRelationIds = JSON.stringify(parsed.data.data.relation_ids);
              return existingReportRelationIds === newReportRelationIds;
            });
            
            const newReport = {
              status: parsed.data.status,
              data: {
                ...parsed.data.data,
                answers: sortAnswersWithSubquestions(parsed.data.data.answers),
              }
            }
            
            // Overwrite existing report
            if(existingIndex !== -1) {
              state.scheduledReports[existingIndex] = newReport;
            } 
            else {
              state.scheduledReports.push(newReport);
            } 

            // Update the status of other reports if the new report is active
            if(newReport.status === reportStatus.ACTIVE) {
              state.scheduledReports = state.scheduledReports.map(oldReport => {
                if(oldReport.data.id !== newReport.data.id && oldReport.status === reportStatus.ACTIVE) {
                  oldReport.status = reportStatus.INACTIVE;
                }
                return oldReport;
              })
            }
          });
        },

        /**
         * This function removes a report from the scheduled reports store.
         * 
         * @param {number} reportId - The ID of the report to remove.
         * @returns {void} A promise that resolves when the report is successfully removed. 
         */
        removeReport: (reportId) => {
          set((state) => {
            state.scheduledReports = state.scheduledReports.filter(report => report.data.id !== reportId);
          });
        },

        /**
         * Updates the status of a specific report within the scheduled reports.
         *
         * @param {string} reportId - The ID of the report to update.
         * @param {'inactive' | 'active' | 'completed_offline'} status - The new status to set for the report.
         * @returns {Promise<void>} A promise that resolves when the report status is successfully updated.
         */
        updateReportStatus: async (reportId, status) => {
          set((state) => {
            const report = state.scheduledReports.find(report => report.data.id === reportId);
            if (!report) {
              console.error(`Report with ID ${reportId} not found.`);
              return;
            }
  
            report.status = status;

            // Update the status of other reports if the new report is active
            if(status === reportStatus.ACTIVE) {
              state.scheduledReports = state.scheduledReports.map(oldReport => {
                if(oldReport.data.id !== reportId && oldReport.status === reportStatus.ACTIVE) {
                  oldReport.status = reportStatus.INACTIVE;
                }
                return oldReport;
              })
            }
          });
        },

        /**
         * Updates a specific answer within a scheduled report.
         * Handles both main answers and subquestions.
         *
         * @param {number} reportId - The ID of the report containing the answer.
         * @param {number} answerId - The ID of the answer to update.
         * @param {string} answerValue - The new value to set for the answer.
         */
        updateReportAnswer: async (reportId, answerId, answerValue) => {
          set((state) => {

            console.log('HIT updateReportAnswer');

            // Vind het specifieke rapport
            const report = state.scheduledReports.find(report => report.data.id === reportId);
            if (!report) {
              console.error(`Report with ID ${reportId} not found.`);
              return;
            }

            const answers = report.data.answers;
            const answerIndex = answers.findIndex(a => a.id === answerId);

            if (answerIndex !== -1) {
              // Update het hoofdantwoord
              report.data.answers[answerIndex].answer = answerValue;
            } 
            else {
              console.error(`Answer with ID ${answerId} not found.`);
            }
          });
        },

        /**
         * Stops a specific report and updates the stop information.
         * It first parses the stop information before setting it.
         * 
         * @param {number} reportId - The ID of the report to stop.
         * @param {{ explanation: string, generateReport: boolean, emails: string }} stopInfo - The stop information to set for the report.
         * @returns {Promise<void>} A promise that resolves when the report is successfully stopped.
         */
        stopReport: async (reportId, stopInfo) => {
          set((state) => {

            const report = state.scheduledReports.find(report => report.data.id === reportId);
            if (!report) {
              console.error(`Report with ID ${reportId} not found.`);
              return;
            }

            report.status = reportStatus.STOPPED;
            const parsedStopInfo = reportStopInfoSchema.safeParse(stopInfo);
            if (parsedStopInfo.success) {
              report.stopInfo = parsedStopInfo.data;
            }
          })
        },

      })
    ),
    {
      name: 'bsp:report-store',
      storage: createJSONStorage(() => ({
        getItem: async (name) => {
          return await localforage.getItem(name);
        },
        setItem: async (name, value) => {
          return await localforage.setItem(name, value);
        },
        removeItem: async (name) => {
          return await localforage.removeItem(name);
        },
      })),
      partialize: (state) => ({ 
        scheduledReports: state.scheduledReports,
        lastSync: state.lastSync,
        schedulingJobActive: state.schedulingJobActive,
      }),
      merge: (persisted, current) => merge(current, persisted)
    }
  )
)